import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSidePanel } from '../../context/SidePanelContext';
import { getDirectoryStocks, getDirectoryIndices, getDirectoryIndex, syncDirectory } from '../../api/client';
import IconRefresh from '../ui/IconRefresh';

// ── Module-level cache (survives sub-tab switches) ────────────────────────────
let _DIR_CACHE    = null; // { stocks, indices, syncedAt }
let _IDX_CACHE    = {};   // { [indexId]: { detail, fetchedAt } }
const IDX_TTL_MS  = 30 * 60 * 1000; // 30 min TTL for index detail

export function clearDirectoryCache() {
  _DIR_CACHE = null;
  _IDX_CACHE = {};
}

// ── Column definitions ────────────────────────────────────────────────────────
const BROAD_COLS = [
  { id: 'nifty50',          label: '50',      title: 'NIFTY 50' },
  { id: 'niftynext50',      label: 'NXT50',   title: 'NIFTY NEXT 50' },
  { id: 'nifty100',         label: '100',     title: 'NIFTY 100' },
  { id: 'nifty200',         label: '200',     title: 'NIFTY 200' },
  { id: 'nifty500',         label: '500',     title: 'NIFTY 500' },
  { id: 'niftymidcap50',    label: 'Mid50',   title: 'NIFTY Midcap 50' },
  { id: 'niftymidcap100',   label: 'Mid100',  title: 'NIFTY Midcap 100' },
  { id: 'niftymidcap150',   label: 'Mid150',  title: 'NIFTY Midcap 150' },
  { id: 'niftysmallcap50',  label: 'Sml50',   title: 'NIFTY Smallcap 50' },
  { id: 'niftysmallcap100', label: 'Sml100',  title: 'NIFTY Smallcap 100' },
  { id: 'niftysmallcap250', label: 'Sml250',  title: 'NIFTY Smallcap 250' },
  { id: 'niftymicrocap250', label: 'Mic250',  title: 'NIFTY Microcap 250' },
  { id: 'niftylargemid250', label: 'LgMd250', title: 'NIFTY LargeMidcap 250' },
  { id: 'niftytotalmarket', label: 'Total',   title: 'NIFTY Total Market' },
];
const SECTORAL_COLS = [
  { id: 'niftybank',        label: 'Bank',    title: 'NIFTY Bank' },
  { id: 'niftyit',          label: 'IT',      title: 'NIFTY IT' },
  { id: 'niftyauto',        label: 'Auto',    title: 'NIFTY Auto' },
  { id: 'niftypharma',      label: 'Pharma',  title: 'NIFTY Pharma' },
  { id: 'niftyfmcg',        label: 'FMCG',    title: 'NIFTY FMCG' },
  { id: 'niftymetal',       label: 'Metal',   title: 'NIFTY Metal' },
  { id: 'niftyrealty',      label: 'Realty',  title: 'NIFTY Realty' },
  { id: 'niftyenergy',      label: 'Energy',  title: 'NIFTY Energy' },
  { id: 'niftyinfra',       label: 'Infra',   title: 'NIFTY Infra' },
  { id: 'niftyconsdurab',   label: 'ConDur',  title: 'NIFTY Consumer Dur.' },
  { id: 'niftyfinserv',     label: 'FinSrv',  title: 'NIFTY Fin Services' },
  { id: 'niftyhealthcare',  label: 'Health',  title: 'NIFTY Healthcare' },
  { id: 'niftyoilgas',      label: 'O&G',     title: 'NIFTY Oil & Gas' },
  { id: 'niftymedia',       label: 'Media',   title: 'NIFTY Media' },
  { id: 'niftypvtbank',     label: 'PvtBnk',  title: 'NIFTY Private Bank' },
  { id: 'niftypsubank',     label: 'PSUBnk',  title: 'NIFTY PSU Bank' },
];
const THEMATIC_COLS = [
  { id: 'niftypse',          label: 'PSE',    title: 'NIFTY PSE' },
  { id: 'niftycpse',         label: 'CPSE',   title: 'NIFTY CPSE' },
  { id: 'niftymnc',          label: 'MNC',    title: 'NIFTY MNC' },
  { id: 'niftydefence',      label: 'Def',    title: 'NIFTY India Defence' },
  { id: 'niftyalpha50',      label: 'Alph50', title: 'NIFTY Alpha 50' },
  { id: 'niftyindiadigital', label: 'Dgtl',   title: 'NIFTY India Digital' },
];
const SYNTHETIC_COLS = [
  { id: 'synth_cement',        label: 'Cem',  title: 'Cement (Synthetic)' },
  { id: 'synth_chemicals',     label: 'Chem', title: 'Chemicals (Synthetic)' },
  { id: 'synth_technology',    label: 'Tech', title: 'Technology (Synthetic)' },
  { id: 'synth_telecom',       label: 'Tel',  title: 'Telecom (Synthetic)' },
  { id: 'synth_retail',        label: 'Ret',  title: 'Retail (Synthetic)' },
  { id: 'synth_aviation',      label: 'Avn',  title: 'Aviation (Synthetic)' },
  { id: 'synth_conglomerates', label: 'Cong', title: 'Conglomerates (Synthetic)' },
  { id: 'synth_ports',         label: 'Port', title: 'Ports & Logistics (Synthetic)' },
];

const ALL_COLS_BY_GROUP = {
  broad:     BROAD_COLS,
  sectoral:  SECTORAL_COLS,
  thematic:  THEMATIC_COLS,
  synthetic: SYNTHETIC_COLS,
};

// Category display metadata
const CAT_META = {
  broad:     { label: 'Broad Market',  color: 'var(--amber-ink)',  textColor: 'var(--amber-ink)' },
  sectoral:  { label: 'Sectoral',      color: 'var(--up-field-ink)',      textColor: 'var(--up-field-ink)' },
  thematic:  { label: 'Thematic',      color: 'var(--amber)', textColor: 'var(--amber)' },
  synthetic: { label: 'Synthetic',     color: '#a78bfa',        textColor: '#a78bfa' },
};

// Index explorer: left panel groups in display order
const EXPLORER_GROUPS = [
  { key: 'broad',     label: 'Broad Market' },
  { key: 'sectoral',  label: 'Sectoral' },
  { key: 'thematic',  label: 'Thematic' },
  { key: 'synthetic', label: 'Synthetic' },
];

// ── Small helpers ─────────────────────────────────────────────────────────────

function fmtSyncedAt(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso.slice(0, 10); }
}

function SynthBadge() {
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '1px 3px', borderRadius: 2,
      background: 'rgba(167,139,250,.15)', color: '#a78bfa',
      marginLeft: 4, verticalAlign: 'middle',
    }}>S</span>
  );
}

function FOBadge() {
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '1px 4px', borderRadius: 2,
      background: 'rgba(245,158,11,.12)', color: 'var(--amber)',
    }}>F&O</span>
  );
}

// Colored dot — filled = in index, empty = not in index
function IndexDot({ filled, color }) {
  return (
    <span style={{
      display: 'inline-block',
      width: 6, height: 6, borderRadius: '50%',
      background:  filled ? color : 'transparent',
      border:      filled ? 'none' : '.5px solid var(--hair-strong)',
      verticalAlign: 'middle',
      flexShrink: 0,
    }} />
  );
}

// Sort arrow indicator
function SA({ col, sortCol }) {
  if (sortCol.col !== col) return <span className="sa">↕</span>;
  return <span className="sa" style={{ opacity: 1, color: 'var(--amber-ink)' }}>{sortCol.dir > 0 ? '↑' : '↓'}</span>;
}

// Empty / loading state
function EmptyState({ loading, onSync, syncing }) {
  return (
    <div style={{
      textAlign: 'center', padding: '60px 24px',
      color: 'var(--ink-3)', fontSize: 11,
    }}>
      {loading ? (
        <div>Loading NSE Directory…</div>
      ) : (
        <>
          <div style={{ marginBottom: 6 }}>NSE Directory has not been synced yet.</div>
          <div style={{ fontSize: 11, marginBottom: 16, color: 'var(--ink-3)' }}>
            This downloads constituent lists for ~40 NSE indices from NSE archives.<br/>
            Takes about 30–60 seconds. Runs automatically every Sunday at 10am IST.
          </div>
          <button className="pill on" onClick={onSync} disabled={syncing}
            style={{ fontSize: 11, padding: '4px 14px' }}>
            {syncing ? '⟳ Syncing…' : '⟳ Sync Now'}
          </button>
        </>
      )}
    </div>
  );
}

// ── Stock Directory view ──────────────────────────────────────────────────────

function StockDirectoryView({ stocks, indices, onTickerClick }) {
  const [search,        setSearch]        = useState('');
  const [filterFO,      setFilterFO]      = useState(false);
  const [filterIdx,     setFilterIdx]     = useState('');
  const [visibleGroups, setVisibleGroups] = useState(['broad']);
  const [sortCol,       setSortCol]       = useState({ col: 'symbol', dir: 1 });

  // Build an index metadata lookup for the filter dropdown
  const indexMeta = useMemo(() => {
    const m = {};
    indices.forEach(ix => { m[ix.index_id] = ix; });
    return m;
  }, [indices]);

  const onSort = useCallback(col =>
    setSortCol(s => s.col === col ? { col, dir: -s.dir } : { col, dir: 1 }), []);

  const toggleGroup = useCallback(g =>
    setVisibleGroups(prev =>
      prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]
    ), []);

  // Which columns to show
  const visibleCols = useMemo(() => {
    const cols = [];
    ['broad', 'sectoral', 'thematic', 'synthetic'].forEach(g => {
      if (visibleGroups.includes(g)) cols.push(...ALL_COLS_BY_GROUP[g]);
    });
    return cols;
  }, [visibleGroups]);

  // Group header spans — consecutive cols of the same category
  const groupSpans = useMemo(() => {
    const spans = [];
    let i = 0;
    while (i < visibleCols.length) {
      const cat = _colCategory(visibleCols[i].id);
      let j = i + 1;
      while (j < visibleCols.length && _colCategory(visibleCols[j].id) === cat) j++;
      spans.push({ cat, count: j - i });
      i = j;
    }
    return spans;
  }, [visibleCols]);

  // Filtered + sorted stocks
  const filtered = useMemo(() => {
    let r = stocks;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      r = r.filter(s =>
        s.symbol.toLowerCase().includes(q) ||
        (s.company_name || '').toLowerCase().includes(q)
      );
    }
    if (filterFO) r = r.filter(s => s.is_fo);
    if (filterIdx) r = r.filter(s => s.memberships.includes(filterIdx));
    return [...r].sort((a, b) => {
      let ak, bk;
      if      (sortCol.col === 'symbol')     { ak = a.symbol;                          bk = b.symbol; }
      else if (sortCol.col === 'company')    { ak = a.company_name || a.symbol;        bk = b.company_name || b.symbol; }
      else if (sortCol.col === 'sector_nse') { ak = a.sector_nse || '';               bk = b.sector_nse || ''; }
      else if (sortCol.col === 'sector_ours'){ ak = a.sector_ours || a.sector_nse||''; bk = b.sector_ours || b.sector_nse||''; }
      else return 0;
      return ak.localeCompare(bk) * sortCol.dir;
    });
  }, [stocks, search, filterFO, filterIdx, sortCol]);

  // Sticky column left offsets
  const W_TICKER  = 72;
  const W_COMPANY = 168;
  const W_SECTOR  = 120;

  const thBase = {
    background: 'var(--surface-2)', color: 'var(--ink-3)',
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '.04em', padding: '4px 6px', whiteSpace: 'nowrap',
    borderBottom: '.5px solid var(--hair)', cursor: 'pointer', userSelect: 'none',
    position: 'sticky', top: 0, zIndex: 3,
  };
  const thGrpBase = {
    background: 'var(--surface-3)', fontSize: 11, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '.05em',
    padding: '3px 6px', whiteSpace: 'nowrap', textAlign: 'center',
    borderBottom: '.5px solid var(--hair)',
    position: 'sticky', top: 0, zIndex: 3, height: 22, boxSizing: 'border-box',
  };

  return (
    <div>
      {/* ── Controls ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        padding: '8px 0 6px', borderBottom: '.5px solid var(--hair)',
        marginBottom: 8,
      }}>
        <input
          className="inp" placeholder="Search ticker or company…"
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ width: 200 }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 11, color: 'var(--ink-3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>
            Filter:
          </span>
          <button className={`pill${filterFO ? ' on' : ''}`} onClick={() => setFilterFO(f => !f)}>
            F&O Only
          </button>
          <select
            className="inp" value={filterIdx} onChange={e => setFilterIdx(e.target.value)}
            style={{ fontSize: 11 }}
          >
            <option value="">All Indices</option>
            {['broad','sectoral','thematic','synthetic'].map(cat => (
              <optgroup key={cat} label={CAT_META[cat].label}>
                {indices.filter(ix => ix.category === cat).map(ix => (
                  <option key={ix.index_id} value={ix.index_id}>
                    {ix.label} ({ix.stock_count})
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
          <span style={{ fontSize: 11, color: 'var(--ink-3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>
            Columns:
          </span>
          {['broad','sectoral','thematic','synthetic'].map(g => (
            <button
              key={g}
              className={`pill${visibleGroups.includes(g) ? ' on' : ''}`}
              onClick={() => toggleGroup(g)}
              style={{ borderColor: visibleGroups.includes(g) ? CAT_META[g].color : undefined,
                       color:       visibleGroups.includes(g) ? CAT_META[g].textColor : undefined }}
            >
              {CAT_META[g].label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Legend ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, fontSize: 11, color: 'var(--ink-3)' }}>
        <span>{filtered.length} stocks</span>
        {Object.entries(CAT_META).map(([cat, meta]) =>
          visibleGroups.includes(cat) ? (
            <span key={cat} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <IndexDot filled color={meta.color} />{meta.label}
            </span>
          ) : null
        )}
        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <IndexDot filled={false} />Not in index
        </span>
      </div>

      {/* ── Table ── */}
      <div className="tw" style={{ maxHeight: '65vh', overflowY: 'auto', overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: W_TICKER + W_COMPANY + W_SECTOR + visibleCols.length * 22 + 38 }}>
          <thead>
            {/* Group header row */}
            <tr>
              {/* Sticky frozen headers — span 3 cols */}
              <th colSpan={3}
                style={{ ...thGrpBase, textAlign: 'left', color: 'var(--ink-3)',
                  position: 'sticky', top: 0, left: 0, zIndex: 6,
                  minWidth: W_TICKER + W_COMPANY + W_SECTOR, background: 'var(--surface-3)',
                  borderRight: '.5px solid var(--hair-strong)',
                }}>
                STOCK INFO
              </th>
              {/* Dynamic group headers */}
              {groupSpans.map((gs, i) => (
                <th key={i} colSpan={gs.count}
                  style={{ ...thGrpBase, color: CAT_META[gs.cat].textColor,
                    borderLeft: i > 0 || true ? '.5px solid var(--hair)' : 'none' }}>
                  {CAT_META[gs.cat].label}
                </th>
              ))}
              {/* F&O flag header */}
              <th style={{ ...thGrpBase, color: 'var(--amber)',
                borderLeft: '.5px solid var(--hair-strong)', minWidth: 36 }}>
                FLAGS
              </th>
            </tr>
            {/* Column header row */}
            <tr>
              {/* Ticker */}
              <th onClick={() => onSort('symbol')}
                style={{ ...thBase, left: 0, zIndex: 5, position: 'sticky',
                  minWidth: W_TICKER, width: W_TICKER, textAlign: 'left',
                  top: 22, background: 'var(--surface-2)',
                }}>
                TICKER <SA col="symbol" sortCol={sortCol} />
              </th>
              {/* Company */}
              <th onClick={() => onSort('company')}
                style={{ ...thBase, left: W_TICKER, zIndex: 5, position: 'sticky',
                  minWidth: W_COMPANY, width: W_COMPANY, textAlign: 'left',
                  top: 22, background: 'var(--surface-2)',
                }}>
                COMPANY <SA col="company" sortCol={sortCol} />
              </th>
              {/* Sector */}
              <th onClick={() => onSort('sector_nse')}
                style={{ ...thBase, left: W_TICKER + W_COMPANY, zIndex: 5, position: 'sticky',
                  minWidth: W_SECTOR, width: W_SECTOR, textAlign: 'left',
                  borderRight: '.5px solid var(--hair-strong)',
                  top: 22, background: 'var(--surface-2)',
                }}>
                SECTOR <SA col="sector_nse" sortCol={sortCol} />
              </th>
              {/* Flag columns */}
              {visibleCols.map(col => (
                <th key={col.id} title={col.title}
                  style={{ ...thBase, top: 22, textAlign: 'center',
                    minWidth: 22, width: 22, padding: '4px 2px',
                    color: CAT_META[_colCategory(col.id)].textColor,
                  }}>
                  {col.label}
                </th>
              ))}
              {/* F&O */}
              <th style={{ ...thBase, top: 22, textAlign: 'center', minWidth: 36,
                borderLeft: '.5px solid var(--hair-strong)', color: 'var(--amber)' }}>
                F&O
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(stock => {
              const memberSet = new Set(stock.memberships);
              return (
                <tr key={stock.symbol} style={{ borderBottom: '.5px solid var(--hair)', background: 'var(--surface)' }}>
                  {/* Ticker */}
                  <td style={{
                    position: 'sticky', left: 0, background: 'inherit', zIndex: 2,
                    padding: '4px 6px', minWidth: W_TICKER, width: W_TICKER, textAlign: 'left',
                  }}>
                    <span className="tlink" onClick={() => onTickerClick(stock.symbol)}>
                      {stock.symbol}
                    </span>
                  </td>
                  {/* Company */}
                  <td style={{
                    position: 'sticky', left: W_TICKER, background: 'inherit', zIndex: 2,
                    padding: '4px 6px', minWidth: W_COMPANY, width: W_COMPANY,
                    textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    color: 'var(--ink-2)',
                  }} title={stock.company_name || ''}>
                    {stock.company_name || '—'}
                  </td>
                  {/* Sector */}
                  <td style={{
                    position: 'sticky', left: W_TICKER + W_COMPANY, background: 'inherit', zIndex: 2,
                    padding: '4px 6px', minWidth: W_SECTOR, width: W_SECTOR,
                    textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    color: 'var(--ink-3)', fontSize: 11,
                    borderRight: '.5px solid var(--hair-strong)',
                  }} title={[stock.sector_nse, stock.sector_ours].filter(Boolean).join(' / ')}>
                    {stock.sector_ours || stock.sector_nse || '—'}
                  </td>
                  {/* Flag dots */}
                  {visibleCols.map(col => {
                    const filled  = memberSet.has(col.id);
                    const cat     = _colCategory(col.id);
                    const color   = CAT_META[cat].color;
                    return (
                      <td key={col.id} style={{ textAlign: 'center', padding: '4px 2px',
                        borderBottom: '.5px solid var(--hair)' }}>
                        <IndexDot filled={filled} color={color} />
                      </td>
                    );
                  })}
                  {/* F&O */}
                  <td style={{ textAlign: 'center', padding: '4px 4px',
                    borderLeft: '.5px solid var(--hair-strong)' }}>
                    {stock.is_fo ? <FOBadge /> : null}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={3 + visibleCols.length + 1}
                  style={{ textAlign: 'center', padding: '28px', color: 'var(--ink-3)', fontSize: 11 }}>
                  No stocks match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Returns the group category for a column ID
function _colCategory(colId) {
  if (BROAD_COLS.find(c => c.id === colId))     return 'broad';
  if (SECTORAL_COLS.find(c => c.id === colId))  return 'sectoral';
  if (THEMATIC_COLS.find(c => c.id === colId))  return 'thematic';
  return 'synthetic';
}

// ── Index Explorer view ───────────────────────────────────────────────────────

function IndexExplorerView({ indices, onTickerClick }) {
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail]         = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSort, setDetailSort] = useState({ col: 'rank', dir: 1 });

  // Select first index on mount
  useEffect(() => {
    if (indices.length > 0 && !selectedId) {
      const first = indices[0];
      setSelectedId(first.index_id);
    }
  }, [indices, selectedId]);

  // Fetch detail when selection changes
  useEffect(() => {
    if (!selectedId) return;
    const cached = _IDX_CACHE[selectedId];
    if (cached && (Date.now() - cached.fetchedAt) < IDX_TTL_MS) {
      setDetail(cached.detail);
      return;
    }
    setDetailLoading(true);
    setDetail(null);
    getDirectoryIndex(selectedId)
      .then(res => {
        if (!res._error) {
          _IDX_CACHE[selectedId] = { detail: res, fetchedAt: Date.now() };
          setDetail(res);
        }
      })
      .catch(() => {})
      .finally(() => setDetailLoading(false));
  }, [selectedId]);

  // Sort detail stocks
  const sortedStocks = useMemo(() => {
    if (!detail?.stocks) return [];
    return [...detail.stocks].sort((a, b) => {
      if (detailSort.col === 'rank')     return ((a.rank || 999) - (b.rank || 999)) * detailSort.dir;
      if (detailSort.col === 'symbol')   return a.symbol.localeCompare(b.symbol) * detailSort.dir;
      if (detailSort.col === 'weight')   return ((b.weightage || 0) - (a.weightage || 0)) * detailSort.dir;
      if (detailSort.col === 'sector')   return (a.sector_nse || '').localeCompare(b.sector_nse || '') * detailSort.dir;
      return 0;
    });
  }, [detail, detailSort]);

  const onDetailSort = col =>
    setDetailSort(s => s.col === col ? { col, dir: -s.dir } : { col, dir: 1 });

  const grouped = useMemo(() => {
    const g = { broad: [], sectoral: [], thematic: [], synthetic: [] };
    indices.forEach(ix => {
      if (g[ix.category]) g[ix.category].push(ix);
    });
    return g;
  }, [indices]);

  const thBase = {
    background: 'var(--surface-2)', color: 'var(--ink-3)',
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '.04em', padding: '4px 8px', whiteSpace: 'nowrap',
    borderBottom: '.5px solid var(--hair)', cursor: 'pointer',
    userSelect: 'none', textAlign: 'left',
  };

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '220px 1fr', gap: 0,
      border: '.5px solid var(--hair)', borderRadius: 10,
      background: 'var(--surface)', minHeight: 500, overflow: 'hidden',
    }}>
      {/* ── Left panel: index list ── */}
      <div style={{
        borderRight: '.5px solid var(--hair)',
        overflowY: 'auto', maxHeight: '72vh',
      }}>
        {EXPLORER_GROUPS.map(({ key, label }) => (
          grouped[key]?.length > 0 && (
            <div key={key}>
              <div style={{
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '.07em', color: CAT_META[key].textColor,
                padding: '6px 10px 4px',
                background: 'var(--surface-2)',
                borderBottom: '.5px solid var(--hair)',
                position: 'sticky', top: 0, zIndex: 2,
              }}>
                {label}
              </div>
              {grouped[key].map(ix => (
                <div key={ix.index_id}
                  onClick={() => setSelectedId(ix.index_id)}
                  style={{
                    padding: '6px 12px',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    borderBottom: '.5px solid var(--hair)',
                    borderLeft: selectedId === ix.index_id
                      ? `2px solid ${CAT_META[key].color}` : '2px solid transparent',
                    background: selectedId === ix.index_id ? 'var(--surface-2)' : 'transparent',
                    fontSize: 11,
                    color: selectedId === ix.index_id ? 'var(--ink)' : 'var(--ink-2)',
                    transition: 'background .1s',
                  }}>
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    {ix.label}
                    {ix.category === 'synthetic' && <SynthBadge />}
                  </span>
                  <span style={{
                    fontSize: 11, color: 'var(--ink-3)',
                    fontFamily: 'var(--font-data)',
                    background: 'var(--surface-2)',
                    padding: '1px 5px', borderRadius: 3,
                    flexShrink: 0, marginLeft: 6,
                  }}>
                    {ix.stock_count || '—'}
                  </span>
                </div>
              ))}
            </div>
          )
        ))}
      </div>

      {/* ── Right panel: detail ── */}
      <div style={{ padding: '14px 16px', overflowY: 'auto', maxHeight: '72vh' }}>
        {!selectedId && (
          <div style={{ color: 'var(--ink-3)', fontSize: 11, paddingTop: 24 }}>
            Select an index from the left panel.
          </div>
        )}
        {selectedId && detailLoading && (
          <div style={{ color: 'var(--ink-3)', fontSize: 11, paddingTop: 24 }}>
            Loading…
          </div>
        )}
        {detail && !detailLoading && (
          <>
            {/* Header */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>
                  {detail.label}
                </span>
                {detail.is_synthetic && <SynthBadge />}
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.7 }}>
                <span style={{
                  textTransform: 'uppercase', fontWeight: 700, letterSpacing: '.05em',
                  color: CAT_META[detail.category]?.textColor || 'var(--ink-3)',
                }}>
                  {detail.category}
                </span>
                {' · '}
                <span>{detail.stock_count} constituents</span>
                {detail.is_synthetic && (
                  <span> · Dollar-volume weighted composite · No dedicated NSE index</span>
                )}
                {detail.last_synced && (
                  <span> · Synced {fmtSyncedAt(detail.last_synced)}</span>
                )}
              </div>
            </div>

            {/* Constituents table */}
            <div className="tw" style={{ maxHeight: '58vh', overflowY: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ ...thBase, width: 30, color: 'var(--ink-3)' }}
                      onClick={() => onDetailSort('rank')}>
                      # <SA col="rank" sortCol={detailSort} />
                    </th>
                    <th style={{ ...thBase, minWidth: 80 }}
                      onClick={() => onDetailSort('symbol')}>
                      TICKER <SA col="symbol" sortCol={detailSort} />
                    </th>
                    <th style={{ ...thBase, minWidth: 160 }}>COMPANY</th>
                    <th style={{ ...thBase, minWidth: 110 }}
                      onClick={() => onDetailSort('sector')}>
                      SECTOR <SA col="sector" sortCol={detailSort} />
                    </th>
                    {!detail.is_synthetic && (
                      <th style={{ ...thBase, minWidth: 100, textAlign: 'right' }}
                        onClick={() => onDetailSort('weight')}>
                        WEIGHT % <SA col="weight" sortCol={detailSort} />
                      </th>
                    )}
                    <th style={{ ...thBase, minWidth: 40, textAlign: 'center' }}>F&O</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedStocks.map((s, i) => (
                    <tr key={s.symbol}
                      style={{ borderBottom: '.5px solid var(--hair)', background: 'var(--surface)' }}>
                      <td style={{ padding: '4px 6px', color: 'var(--ink-3)', fontFamily: 'var(--font-data)', fontSize: 11 }}>
                        {s.rank || i + 1}
                      </td>
                      <td style={{ padding: '4px 6px', textAlign: 'left' }}>
                        <span className="tlink" onClick={() => onTickerClick(s.symbol)}>
                          {s.symbol}
                        </span>
                      </td>
                      <td style={{ padding: '4px 6px', textAlign: 'left', color: 'var(--ink-2)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}
                        title={s.company_name || ''}>
                        {s.company_name || '—'}
                      </td>
                      <td style={{ padding: '4px 6px', textAlign: 'left', color: 'var(--ink-3)', fontSize: 11 }}>
                        {s.sector_ours || s.sector_nse || '—'}
                      </td>
                      {!detail.is_synthetic && (
                        <td style={{ padding: '4px 8px', textAlign: 'right',
                          fontFamily: 'var(--font-data)' }}>
                          {s.weightage != null ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                              <span style={{
                                display: 'inline-block',
                                width: Math.max(2, Math.min(48, (s.weightage / (sortedStocks[0]?.weightage || 1)) * 48)),
                                height: 4, borderRadius: 2,
                                background: 'var(--amber-ink)', opacity: 0.55,
                                verticalAlign: 'middle',
                              }} />
                              <span style={{ color: 'var(--ink-2)', fontSize: 11 }}>
                                {s.weightage.toFixed(2)}%
                              </span>
                            </span>
                          ) : '—'}
                        </td>
                      )}
                      <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                        {s.is_fo ? <FOBadge /> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main tab component ────────────────────────────────────────────────────────

// ── Info popover for NSE Directory ───────────────────────────────────────────
function DirInfoPopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ fontSize: 11, background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--ink-3)', padding: '0 2px', lineHeight: 1 }}>ⓘ</button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 100, marginTop: 4,
          background: 'var(--surface)', border: '.5px solid var(--hair)',
          borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,.15)',
          padding: '8px 10px', minWidth: 300, maxWidth: 380,
          fontSize: 11, color: 'var(--ink-2)', lineHeight: 1.6,
        }}>
          NSE stock and index directory. Stock Directory shows membership across broad market, sectoral, thematic, and synthetic indices. Index Explorer shows index constituents with weight %. Data synced via the Sync NSE button.
        </div>
      )}
    </span>
  );
}

export default function NSEDirectoryTab() {
  const { open: openPanel } = useSidePanel();

  const [view,      setView]      = useState('stock'); // 'stock' | 'index'
  const [stocks,    setStocks]    = useState([]);
  const [indices,   setIndices]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [syncing,   setSyncing]   = useState(false);
  const [syncedAt,  setSyncedAt]  = useState(null);

  // Load data from cache or API
  const loadData = useCallback(async () => {
    if (_DIR_CACHE) {
      setStocks(_DIR_CACHE.stocks);
      setIndices(_DIR_CACHE.indices);
      setSyncedAt(_DIR_CACHE.syncedAt);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [stocksRes, idxRes] = await Promise.all([
        getDirectoryStocks(),
        getDirectoryIndices(),
      ]);
      const s  = stocksRes.stocks  || [];
      const ix = idxRes.indices    || [];
      _DIR_CACHE = { stocks: s, indices: ix, syncedAt: stocksRes.synced_at };
      setStocks(s);
      setIndices(ix);
      setSyncedAt(stocksRes.synced_at);
    } catch (e) {
      console.warn('[Directory] load failed:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await syncDirectory();
      // Poll after 5s to show partial data, then again at 40s for full data
      setTimeout(() => {
        clearDirectoryCache();
        loadData();
      }, 5000);
      setTimeout(() => {
        clearDirectoryCache();
        loadData().finally(() => setSyncing(false));
      }, 45000);
    } catch (e) {
      setSyncing(false);
    }
  };

  const handleTickerClick = useCallback(symbol => {
    openPanel({ name: symbol });
  }, [openPanel]);

  const isEmpty = !loading && stocks.length === 0;

  return (
    <div style={{ paddingTop: 6 }}>
      {/* ── Top bar ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 10, gap: 10, flexWrap: 'wrap',
      }}>
        {/* View toggle + info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div className="seg">
            <button className={`sbtn${view === 'stock' ? ' on' : ''}`}
              onClick={() => setView('stock')}>
              ☰ Stock Directory
            </button>
            <button className={`sbtn${view === 'index' ? ' on' : ''}`}
              onClick={() => setView('index')}>
              ◫ Index Explorer
            </button>
          </div>
          <DirInfoPopover/>
        </div>

        {/* Right: sync info + button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--ink-3)' }}>
          {syncedAt && !loading && (
            <span>Data as of {fmtSyncedAt(syncedAt)}</span>
          )}
          {loading && <span>Loading…</span>}
          {syncing && (
            <span style={{ color: 'var(--amber-ink)' }}>⟳ Syncing from NSE…</span>
          )}
          <button className="pill" onClick={() => { clearDirectoryCache(); loadData(); }}
            disabled={loading || syncing}
            style={{ fontSize: 11, display:'inline-flex', alignItems:'center', gap:5 }}>
            <IconRefresh size={13}/> Refresh
          </button>
          {!syncing && (
            <button className="pill" onClick={handleSync} disabled={loading || syncing}
              style={{ fontSize: 11, display:'inline-flex', alignItems:'center', gap:5 }}>
              <IconRefresh size={13}/> Sync NSE
            </button>
          )}
        </div>
      </div>

      {/* ── Content ── */}
      {(loading || isEmpty) ? (
        <EmptyState loading={loading} onSync={handleSync} syncing={syncing} />
      ) : (
        <>
          {view === 'stock' && (
            <StockDirectoryView
              stocks={stocks}
              indices={indices}
              onTickerClick={handleTickerClick}
            />
          )}
          {view === 'index' && (
            <IndexExplorerView
              indices={indices}
              onTickerClick={handleTickerClick}
            />
          )}
        </>
      )}
    </div>
  );
}
